<template>
  <div class="form-group">
    <label
      v-if="label != null"
      :for="id"
      class="control-label col-sm-2"
    >{{ label }}</label>
    <div
      class="multi-select"
      :class="{ 'has-error': hasErrorMessage, 'col-sm-10': label != null }"
    >
      <multiselect
        :id="id"
        class="multiselect--full-width multiselect--timid multiselect--option-multiline"
        :name="label"
        :options="options"
        :value="value"
        :searchable="true"
        :close-on-select="false"
        :internal-search="false"
        :show-labels="false"
        label="name"
        :track-by="trackBy"
        :multiple="true"
        :clear-on-select="false"
        :hide-selected="true"
        :max="maxSelectedOptions"
        :options-limit="50"
        open-direction="below"
        :placeholder="placeholder"
        :block-keys="['Delete']"
        :loading="isLoading"
        :disabled="disabled"
        autocomplete="off"
        @search-change="changeSearchQuery"
        @input="$emit('input', $event)"
      >
        <template
          slot="tag"
          slot-scope="{ option }"
        >
          <div class="multiselect__tags-wrap">
            <div class="multiselect__tag">
              <div>{{ option.name }}</div>
              <div
                  tabindex="0"
                  class="multiselect__tag-icon"
                  @click="deleteValue(option)"
                  @keydown.enter="deleteValue(option)"
              />
            </div>
          </div>
        </template>

        <template
          v-if="hasCustomOptionSlot"
          slot="option"
          slot-scope="props"
        >
          <slot
            name="customOption"
            :props="props"
          />
        </template>
        <template
          v-if="showButton"
          slot="beforeList"
        >
          <li
            class="multiselect__element"
            @click="selectAllFiltered"
          >
            <span class="multiselect__option multiselect__option--custom">
              {{ trans('core.select_all') }}
            </span>
          </li>
        </template>
      </multiselect>

      <span
        v-if="hasErrorMessage"
        class="help-block"
      >
        <strong>{{ errormessage }}</strong>
      </span>
    </div>
  </div>
</template>

<script>
import Multiselect from 'vue-multiselect'
import { defaultErrorMessage } from '@/helpers'

export default {
  name: 'MultiSelectComponent',

  // OR register locally
  components: { Multiselect },

  props: {
    id: {
      required: true,
      type: [Number, String],
    },
    errormessage: {
      required: false,
      default: null,
    },
    exclude: {
      required: false,
      type: Object,
      default: () => {},
    },
    include: {
      required: false,
      type: Object,
      default: () => {},
    },
    queryOptions: {
      required: false,
      type: Object,
      default: () => {},
    },
    label: {
      required: false,
      type: String,
      default: null,
    },
    maxSelectedOptions: {
      required: false,
      type: Number,
      default: 100,
    },
    placeholder: {
      required: false,
      default: Lang.get('core.type_or_click'),
      type: String,
    },
    type: {
      required: true,
      type: String,
    },
    value: {
      required: false,
      default: () => [],
      type: Array,
      validator: (value) => {
        if (!value) {
          throw new Error(`Value null is invalid, parent component name: ${this.$parent?.$options?.name}`)
        }
      },
    },
    searchAtMounted: {
      required: false,
      type: Boolean,
      default: false,
    },
    trackBy: {
      required: false,
      type: String,
      default: 'id',
    },
    disabled: {
      required: false,
      default: false,
      type: Boolean,
    },
  },

  data () {
    return {
      disableAllButton: true,
      isLoading: false,
      options: [],
      searchQuery: '',
    }
  },

  computed: {
    hasErrorMessage () {
      return this.errormessage !== null
    },
    url () {
      return `/employee/suggest/${this.type}s`
    },
    showButton () {
      return this.options.length > 1 && this.maxSelectedOptions > 1 && this.disableAllButton === false
    },
    hasCustomTagSlot () {
      return !!this.$slots.customTag | !!this.$scopedSlots.customTag
    },
    hasCustomOptionSlot () {
      return !!this.$slots.customOption | !!this.$scopedSlots.customOption
    },
  },
  mounted () {
    $('#' + this.id).attr('autocomplete', 'off')

    if (this.searchAtMounted) {
      this.search()
    }
  },
  methods: {
    // debounce the event. The function() syntax is intentional, otherwise we get "this" problems
    changeSearchQuery: _.debounce(function (val) {
      this.isLoading = true
      this.disableAllButton = true

      this.searchQuery = val

      this.search()
    }, 500),

    deleteValue (val) {
      const newValue = this.value.filter(item => item.id != val.id)

      this.handleInput(newValue)
      // only add value if not already included
      if (!this.options.some(option => option.id === val.id)) {
        this.options.push(val)
      }

    },
    handleInput (val) {
      this.$emit('input', val)
    },
    search () {
      axios.post(this.url, {
        query: this.searchQuery,
        exclude: this.exclude,
        include: this.include,
        ...this.queryOptions,
      }).then(({ data }) => {
        if (this.value) {
          const valueIds = this.value.map(val => val.id)

          this.options = valueIds.length < 1
            ? data
            : data.filter(item => {
              return !valueIds.includes(item.id)
            })
        }
      }).catch(error => defaultErrorMessage(error))
        .finally(() => {
          this.disableAllButton = this.searchQuery == null || this.searchQuery.length < 1

          this.isLoading = false
        })
    },
    selectAllFiltered () {
      const newValue = this.value

      this.disableAllButton = true

      this.options.forEach(option => {
        if (!this.value.includes(option) && this.value.length < this.maxSelectedOptions) {
          newValue.push(option)
        }
      })

      this.handleInput(newValue)
    },
  },
}
</script>

<style scoped>
.multiselect__tag-icon:focus::after {
    content: "\D7";
    color: red;
    font-size: 14px;
}
</style>
